import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { InvoiceState } from "./invoices.entity";
import {
  fetchInvoices,
  fetchInvoiceById,
  updateInvoiceStatus,
  bulkUpdateInvoiceStatus,
} from "./invoices.api";
import type { ChangedInvoiceStatus } from "@/repositories/invoice-records";
import { uuid } from "@/interfaces";

export const getInvoices = createAsyncThunk(
  "invoices/get",
  async (args?: { 
    page?: number; 
    pageSize?: number; 
    filters?: any;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
  }) => {
    const page = args?.page || 1;
    const pageSize = args?.pageSize || 10;
    const sortKey = args?.sortKey;
    const sortOrder = args?.sortOrder || "asc";
    return fetchInvoices(page, pageSize, args?.filters, sortKey, sortOrder);
  }
);

export const getInvoice = createAsyncThunk("invoices/getById", async (id: uuid) => {
  return fetchInvoiceById(id);
});

export const updateInvoiceStatusAction = createAsyncThunk(
  "invoices/updateStatus",
  async ({ id, status }: { id: uuid; status: ChangedInvoiceStatus }) => {
    return updateInvoiceStatus(id, status);
  }
);

export const bulkUpdateInvoiceStatusAction = createAsyncThunk(
  "invoices/bulkUpdateStatus",
  async ({ ids, status }: { ids: uuid[]; status: ChangedInvoiceStatus }) => {
    await bulkUpdateInvoiceStatus(ids, status);
    return { ids, status };
  }
);

const initialState: InvoiceState = {
  list: [],
  loading: false,
  listRequestId: null,
  error: null,
  total: 0,
  currentInvoice: null,
  currentInvoiceRequestId: null,
  currentInvoiceError: null,
  currentInvoiceSettledFor: null,
};

const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    clearCurrentInvoice: (state) => {
      state.currentInvoice = null;
      state.currentInvoiceRequestId = null;
      state.currentInvoiceError = null;
      state.currentInvoiceSettledFor = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInvoices.pending, (state, action) => {
        state.listRequestId = action.meta.requestId;
        state.error = null;
        state.loading = true;
      })
      .addCase(getInvoices.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.list = action.payload.data;
        state.total = action.payload.count;
        state.loading = false;
      })
      .addCase(getInvoices.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.error = action.error.message || "Failed to fetch invoices";
        console.error("Failed to fetch invoices:", action.error);
      })
      .addCase(getInvoice.pending, (state, action) => {
        state.currentInvoiceRequestId = action.meta.requestId;
        state.currentInvoiceError = null;
        state.currentInvoiceSettledFor = null;
        if (state.currentInvoice?.id !== action.meta.arg) state.currentInvoice = null;
      })
      .addCase(getInvoice.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentInvoiceRequestId) return;
        state.currentInvoice = action.payload;
        state.currentInvoiceSettledFor = action.meta.arg;
      })
      .addCase(getInvoice.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentInvoiceRequestId) return;
        const notFound = action.error.code === "not_found";
        if (notFound || state.currentInvoice?.id !== action.meta.arg) state.currentInvoice = null;
        state.currentInvoiceError = notFound ? null : "The invoice could not be loaded.";
        state.currentInvoiceSettledFor = action.meta.arg;
      })
      .addCase(updateInvoiceStatusAction.fulfilled, (state, action) => {
        const { id, status, paid_at } = action.payload;
        state.list = state.list.map((invoice) => (invoice.id === id ? { ...invoice, status, paid_at } : invoice));
        if (state.currentInvoice?.id === id) state.currentInvoice = action.payload;
      });
  },
});

export const { clearCurrentInvoice } = invoicesSlice.actions;

export const invoiceLoad = (state: InvoiceState, id: uuid | null) => ({
  invoice: id !== null && state.currentInvoice?.id === id ? state.currentInvoice : null,
  loading: id !== null && state.currentInvoiceSettledFor !== id,
  error: state.currentInvoiceSettledFor === id ? state.currentInvoiceError : null,
});

export default invoicesSlice.reducer;

